import { useNavigate } from "react-router-dom";
import {
  applyAuthResponse,
  Tokens,
  useAdminImpersonationControllerStart,
  useAdminImpersonationControllerStop,
  useAuthStore,
} from "service";
import { queryClient } from "@/query-client";
import { AdRoutes } from "@/constants/ad-routes";

export interface UseImpersonation {
  start: (organizationId: string) => void;
  exit: () => void;
  isStarting: boolean;
  isExiting: boolean;
}

/**
 * Superadmin "impersonate an organization" mechanics. Both Start and Stop
 * return a fresh AdminLoginResDto for the resulting session, so tokens are
 * simply swapped in place - no separate stash of the superadmin's own tokens
 * is needed.
 */
const useImpersonation = (): UseImpersonation => {
  const navigate = useNavigate();
  const { setIsLoggedIn, setCustomer } = useAuthStore();

  const { mutate: startMutate, isLoading: isStarting } =
    useAdminImpersonationControllerStart({
      onSuccess: ({ data }) => {
        if (!data) return;

        applyAuthResponse(data);
        queryClient.clear();
        navigate(AdRoutes.customer);
      },
    });

  const { mutate: stopMutate, isLoading: isExiting } =
    useAdminImpersonationControllerStop({
      onSuccess: ({ data }) => {
        if (!data) return;

        applyAuthResponse(data);
        queryClient.clear();
        navigate(AdRoutes.organizations);
      },
      onError: () => {
        Tokens.destroy();
        setIsLoggedIn(false);
        setCustomer(null);
        queryClient.clear();
      },
    });

  return {
    start: (organizationId: string) =>
      startMutate({ pathParams: { orgId: organizationId } }),
    exit: () => stopMutate({}),
    isStarting,
    isExiting,
  };
};

export default useImpersonation;
